import { Component, ContentChild, OnInit } from '@angular/core';

import { AbstractControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslocoService } from '@jsverse/transloco';
import { ReplaySubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { HasTitle } from 'app/has-title';
import { configRouteNameValidatorFactory } from 'app/modules/configuration/configuration-utils';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import {
  ConfigurationRoute,
  ConfigurationRouteService,
} from 'app/providers/semantria-api/configuration-route.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ConfigurationRouteViewComponent } from '../configuration-route-view/configuration-route-view.component';
import { ConfigurationRouteListService } from '../../services/configuration-route-list.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-route-list',
  templateUrl: './configuration-route-list.component.html',
  styleUrls: ['./configuration-route-list.component.scss'],
  standalone: false,
})
export class ConfigurationRouteListComponent extends HasTitle implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ContentChild(ConfigurationRouteViewComponent) configurationRouteViewComponent;

  private session: Session;

  canEditRoute: boolean;

  routesRightPlaceholder: { title: string; subtitle: string };

  showCreateConfigurationRoute: boolean;
  createConfigurationRouteData: {
    nameValidator: (control: AbstractControl) => { [key: string]: any };
    groupId: string;
  };
  routes: ConfigurationRoute[];
  selectedRoute: ConfigurationRoute;

  isLoading = true;
  groupId: string;

  routeId$ = new ReplaySubject<string>();
  unsub$ = new Subject();

  languages: { id: string; name: string }[];

  constructor(
    protected titleService: Title,
    public authService: AuthService,
    private configRouteService: ConfigurationRouteService,
    private groupService: GroupService,
    private listService: ConfigurationRouteListService,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: SemantriaAccountService,
    private translocoService: TranslocoService
  ) {
    super(titleService);

    this.listService.onRoutesLoaded
      .pipe(takeUntil(this.unsub$))
      .subscribe((routes) => (this.routes = routes));

    this.listService.onRouteUpdated
      .pipe(takeUntil(this.unsub$))
      .subscribe((r) => (this.selectedRoute = r));

    this.listService.onRouteDeleted.pipe(takeUntil(this.unsub$)).subscribe((r) => {
      if (this.selectedRoute?.route_id === r.route_id) {
        this.selectedRoute = undefined;
      }

      this.routes = this.routes.filter((cr) => cr.route_id !== r.route_id);

      if (!this.selectedRoute && this.routes) {
        this.selectFirstRoute();
      }
    });
  }

  async ngOnInit() {
    this.session = await this.authService.getSession();
    if (!this.groupService.getSelectedGroup()) {
      await this.groupService.refreshAndSelectActiveGroup();
    }
    this.groupId = this.groupService.getSelectedGroupId();
    this.routes = await this.configRouteService.getConfigurationRoutes(this.groupId);
    this.routes.sort((r1, r2) => r1.name.localeCompare(r2.name));

    this.listService.routesLoaded(this.routes);

    this.translocoService
      .selectTranslateObject('configuration-routes.list')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => {
        this.setTitle(translations['title']);

        this.routes.length < 1
          ? (this.routesRightPlaceholder = {
              title: translations['no-route-available'],
              subtitle: translations['create-route-if-not-available'],
            })
          : (this.routesRightPlaceholder = {
              title: translations['no-route-selected'],
              subtitle: translations['select-route-if-not-selected'],
            });
      });

    if (this.routes.length) {
      const routeIdParam = this.route.firstChild?.snapshot.paramMap.get('routeId');
      this.routeId$.next(routeIdParam || this.routes[0].route_id);
    }

    this.translocoService
      .selectTranslate('configuration-routes.list.title-with-selection', {
        route: this.selectedRoute?.name,
      })
      .pipe(takeUntil(this.unsub$))
      .subscribe((translation) => {
        if (this.selectedRoute) {
          this.setTitle(translation);
        }
      });

    this.routeId$
      .pipe(
        takeUntil(this.unsub$),
        filter((id) => !!id)
      )
      .subscribe((id) => {
        this.selectedRoute = this.routes.find((r) => r.route_id === id);
        if (!this.selectedRoute) {
          this.router.navigateByUrl('not-found', { skipLocationChange: true });
        } else {
          this.setTitle(
            this.translocoService.translate('configuration-routes.list.title-with-selection', {
              route: this.selectedRoute?.name,
            })
          );
          this.router.navigate([this.selectedRoute.route_id], { relativeTo: this.route });
        }
      });

    this.listService.onAfterRouteSelected.pipe(takeUntil(this.unsub$)).subscribe((route) => {
      if (route) {
        this.routeId$.next(route.route_id);
      }
    });
    this.canEditRoute = this.authService.canEditConfigurationRoute();

    this.languages = (await this.accountService.sub.latest()).languages;

    this.isLoading = false;
  }

  getLanguageText(id: string): string {
    if (!id) {
      return this.translocoService.translate('configuration-routes.list.no-default-language');
    }
    return this.languages.find((l) => l.id === id).name;
  }

  private selectFirstRoute() {
    this.selectedRoute = this.routes[0];
    const firstRouteId = this.selectedRoute?.route_id || '.';
    this.router.navigate([firstRouteId], { relativeTo: this.route });
  }

  onRouteSelected(route: ConfigurationRoute, event = undefined) {
    if (event?.metaKey || event?.detail?.metaKey) {
      const url = `/groups/${this.groupService.getSelectedGroupId(true)}/configuration-routes/${
        route.route_id
      }`;
      window.open(url, '_blank');
      return;
    }
    this.listService.beforeRouteSelected(route);
  }

  onAddRoute() {
    this.showCreateConfigurationRoute = true;

    this.createConfigurationRouteData = {
      nameValidator: configRouteNameValidatorFactory(this.routes),
      groupId: this.groupId,
    };
  }

  onCreateConfigurationRouteClosed($event) {
    this.showCreateConfigurationRoute = false;

    if ($event) {
      this.routes.push($event);

      this.routes.sort((r1, r2) => r1.name.localeCompare(r2.name));

      this.listService.routesLoaded(this.routes);

      this.onRouteSelected($event);
    }
  }
}
